import { useCallback, useEffect, useRef, useState } from 'react';
import { getReport } from './api';
import type { Report } from './types';

export type ReportState =
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'error' }
  | { status: 'ready'; report: Report };

/** One report. `adopt` takes the report an edit answered with, so the page need not read again. */
export function useReport(reportId: string | undefined) {
  const [state, setState] = useState<ReportState>({ status: 'loading' });
  const [reloads, setReloads] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    if (reportId === undefined) return;
    const mine = ++generation.current;
    setState({ status: 'loading' });
    void getReport(reportId).then((outcome) => {
      if (mine !== generation.current) return;
      if (outcome.ok) setState({ status: 'ready', report: outcome.value });
      else setState({ status: outcome.status === 404 ? 'missing' : 'error' });
    });
  }, [reportId, reloads]);

  const adopt = useCallback((report: Report) => setState({ status: 'ready', report }), []);
  const reload = useCallback(() => setReloads((count) => count + 1), []);
  return { state, adopt, reload } as const;
}
